/**
 * Formate la réponse de `POST .../check_decomposition/` (hoortrade-bo) pour l’UI.
 *
 * Contrat BO : {
 *   best_candidate: Record<pcsRef, stiRef | pcsRef>,
 *   success_message: Record<pcsRef, string>,
 *   error_message: Record<pcsRef, string>,
 *   common_decompositions: Record<stiRef, { quantity, pcs_list, pcs_details, message }>,
 * }
 *
 * @param {object | null | undefined} raw
 * @param {string[]} [orderedReferences] ordre d'affichage (ex. lignes saisies)
 */
export function formatDecompositionResponse(raw, orderedReferences = []) {
  if (raw == null || typeof raw !== 'object') {
    return {
      ok: false,
      serverError: true,
      pcsRows: [],
      commonDecompositions: [],
      summary: { errorCount: 0, warnCount: 0, successCount: 0, infoCount: 0 },
    };
  }

  const best_candidate = raw.best_candidate ?? {};
  const success_message = raw.success_message ?? {};
  const error_message = raw.error_message ?? {};
  const common_decompositions = raw.common_decompositions ?? {};

  const refSet = new Set([
    ...orderedReferences,
    ...Object.keys(best_candidate),
    ...Object.keys(success_message),
    ...Object.keys(error_message),
  ]);
  const refs = orderedReferences.length
    ? [...orderedReferences.filter((r) => refSet.has(r)), ...[...refSet].filter((r) => !orderedReferences.includes(r))]
    : [...refSet];

  const pcsRows = refs.map((ref) => {
    const candidate = best_candidate[ref] ?? null;
    const successMessage = success_message[ref] ?? null;
    const errorMessage = error_message[ref] ?? null;

    /** @type {'error' | 'warning' | 'success' | 'info'} */
    let variant = 'info';
    if (errorMessage && !candidate) {
      variant = 'error';
    } else if (errorMessage && candidate) {
      variant = 'warning';
    } else if (successMessage || candidate) {
      variant = 'success';
    }

    return {
      reference: ref,
      candidate,
      successMessage,
      errorMessage,
      variant,
    };
  });

  const commonDecompositions = Object.entries(common_decompositions).map(
    ([internalProductRef, data]) => ({
      internalProductRef,
      quantity: data?.quantity ?? 0,
      pcsList: Array.isArray(data?.pcs_list) ? data.pcs_list : [],
      pcsDetails: Array.isArray(data?.pcs_details) ? data.pcs_details : [],
      message: data?.message ?? '',
    }),
  );

  const errorCount = pcsRows.filter((r) => r.variant === 'error').length;
  const warnCount = pcsRows.filter((r) => r.variant === 'warning').length;
  const successCount = pcsRows.filter((r) => r.variant === 'success').length;
  const infoCount = pcsRows.filter((r) => r.variant === 'info').length;

  return {
    ok: errorCount === 0,
    serverError: false,
    pcsRows,
    commonDecompositions,
    summary: { errorCount, warnCount, successCount, infoCount },
  };
}
